import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ACTIVITY_SUBJECTS,
  ACTIVITY_SUBJECT_LABELS,
  ACTIVITY_TYPES,
  ACTIVITY_TYPE_LABELS,
  type Activity,
  type ActivityInput,
  type ActivitySubject,
  type ActivityType,
} from '@aimmo/contracts';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RelationPicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { activityEditInput, invalidateActivityViews, updateActivity } from '../lib/activities.js';
import { errorMessage } from '../lib/errors.js';
import { resolveContact, searchContacts } from '../resources/contact-lookups.js';
import { resolveProperty, searchProperties } from '../resources/mandate-lookups.js';
import {
  resolveAppointmentType,
  resolveDemand,
  resolveMandate,
  searchAppointmentTypes,
  searchDemands,
  searchMandates,
} from '../resources/activity-lookups.js';
import { localDateTime } from '../lib/dates.js';

/** Types a person may log. `status_change` is the system's own handwriting. */
const LOGGABLE = ACTIVITY_TYPES.filter((type) => type !== 'status_change');

const SUBJECT_LOOKUPS: Record<
  ActivitySubject,
  {
    search: (
      query: string,
      signal?: AbortSignal,
    ) => Promise<readonly { value: string; label: string }[]>;
    resolve: (value: string) => Promise<{ value: string; label: string } | null>;
  }
> = {
  contact: { search: searchContacts, resolve: resolveContact },
  property: { search: searchProperties, resolve: resolveProperty },
  mandate: { search: searchMandates, resolve: resolveMandate },
  demand: { search: searchDemands, resolve: resolveDemand },
};

export interface ActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fixed by the page that opened it — a property detail knows its subject. */
  subject?: { type: ActivitySubject; id: string };
  /** From a calendar day click: `YYYY-MM-DD`, at nine in the morning. */
  defaultScheduledAt?: string;
  /**
   * The activity to edit. Its subject is fixed — moving a call from one
   * contact to another is a different call — and what the dialog does not show
   * (owner, completion, outcome) is carried over as it was.
   */
  activity?: Activity;
}

/** A stored instant as the `datetime-local` input shows it, or `''`. */
function local(iso: string | null): string {
  return iso ? localDateTime(new Date(iso)) : '';
}

/**
 * One dialog for every kind of activity (U5).
 *
 * Dynamic rather than multi-step: the branches are four, and each adds one or
 * two fields. A wizard would add state and two extra taps on a phone for
 * nothing.
 *
 * The subject picker is the part the timeline never needed. `subject_type` and
 * `subject_id` are not null, and every compose box before this one lived inside
 * a subject already — from the Agenda there is no such context, so the dialog
 * asks, and refuses to submit without an answer.
 */
export function ActivityDialog({
  open,
  onOpenChange,
  subject,
  defaultScheduledAt,
  activity,
}: ActivityDialogProps) {
  const queryClient = useQueryClient();
  const editing = activity !== undefined;
  const fixedSubject = activity ? { type: activity.subjectType, id: activity.subjectId } : subject;

  const [type, setType] = React.useState<ActivityType>(subject ? 'note' : 'appointment');
  const [subjectType, setSubjectType] = React.useState<ActivitySubject>(
    fixedSubject?.type ?? 'property',
  );
  const [subjectId, setSubjectId] = React.useState<string | null>(fixedSubject?.id ?? null);
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [scheduledAt, setScheduledAt] = React.useState('');
  const [endsAt, setEndsAt] = React.useState('');
  const [appointmentTypeId, setAppointmentTypeId] = React.useState<string | null>(null);

  // Opening on a day means that day at nine, which is the hour anybody means
  // when they click a square on a calendar.
  //
  // Editing opens on what is there instead, every time it opens: a dialog
  // reopened after a cancel must not show the half-typed change.
  React.useEffect(() => {
    if (!open) return;
    if (activity) {
      setType(activity.type);
      setTitle(activity.title ?? '');
      setBody(activity.body ?? '');
      setScheduledAt(local(activity.scheduledAt));
      setEndsAt(local(activity.endsAt));
      setAppointmentTypeId(activity.appointmentTypeId);
      return;
    }
    setScheduledAt(defaultScheduledAt ? `${defaultScheduledAt}T09:00` : localDateTime(new Date()));
  }, [open, defaultScheduledAt, activity]);

  const save = useMutation({
    mutationFn: async (input: ActivityInput) => {
      if (activity) return updateActivity(activity.id, input, queryClient);
      const created = assertOk(await api.activities.create({ body: input })).body;
      await invalidateActivityViews(queryClient);
      return created;
    },
    onSuccess: () => {
      if (!editing) reset();
      onOpenChange(false);
    },
  });

  function reset(): void {
    setTitle('');
    setBody('');
    setEndsAt('');
    setAppointmentTypeId(null);
    if (!fixedSubject) setSubjectId(null);
  }

  const needsSchedule = type === 'appointment' || type === 'reminder';
  const canSubmit =
    Boolean(subjectId) &&
    (needsSchedule ? Boolean(scheduledAt) : true) &&
    (type === 'note' || type === 'call' ? Boolean(body.trim()) : true);

  function submit(): void {
    if (!subjectId) return;

    if (activity) {
      save.mutate(
        activityEditInput(activity, {
          type,
          title,
          body,
          scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : '',
          endsAt: endsAt ? new Date(endsAt).toISOString() : '',
          appointmentTypeId,
        }),
      );
      return;
    }

    save.mutate({
      type,
      subjectType,
      subjectId,
      ...(title.trim() ? { title: title.trim() } : {}),
      ...(body.trim() ? { body: body.trim() } : {}),
      ...(needsSchedule && scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
      ...(type === 'appointment' && endsAt ? { endsAt: new Date(endsAt).toISOString() } : {}),
      ...(type === 'appointment' && appointmentTypeId ? { appointmentTypeId } : {}),
    });
  }

  const lookup = SUBJECT_LOOKUPS[subjectType];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Modifica attività' : 'Nuova attività'}</DialogTitle>
          <DialogDescription>
            I campi cambiano con la tipologia: solo quelli che servono a quel tipo.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-3 overflow-y-auto">
          <div className="grid gap-1">
            <Label htmlFor="activity-type">Tipo</Label>
            <Select value={type} onValueChange={(next) => setType(next as ActivityType)}>
              <SelectTrigger id="activity-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOGGABLE.map((value) => (
                  <SelectItem key={value} value={value}>
                    {ACTIVITY_TYPE_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!fixedSubject && (
            <div className="grid gap-2 sm:grid-cols-[10rem_1fr]">
              <div className="grid gap-1">
                <Label htmlFor="activity-subject-type">Riguarda</Label>
                <Select
                  value={subjectType}
                  onValueChange={(next) => {
                    setSubjectType(next as ActivitySubject);
                    // The id belonged to the previous kind of thing.
                    setSubjectId(null);
                  }}
                >
                  <SelectTrigger id="activity-subject-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_SUBJECTS.map((value) => (
                      <SelectItem key={value} value={value}>
                        {ACTIVITY_SUBJECT_LABELS[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1">
                <Label>Soggetto</Label>
                <RelationPicker
                  value={subjectId}
                  onChange={setSubjectId}
                  search={lookup.search}
                  resolve={lookup.resolve}
                  placeholder="Cerca…"
                  aria-label="Soggetto"
                />
              </div>
            </div>
          )}

          {needsSchedule && (
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-1">
                <Label htmlFor="activity-when">{type === 'reminder' ? 'Scadenza' : 'Inizio'}</Label>
                <Input
                  id="activity-when"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(event) => setScheduledAt(event.target.value)}
                />
              </div>

              {type === 'appointment' && (
                <div className="grid gap-1">
                  <Label htmlFor="activity-end">Fine</Label>
                  <Input
                    id="activity-end"
                    type="datetime-local"
                    value={endsAt}
                    onChange={(event) => setEndsAt(event.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {type === 'appointment' && (
            <div className="grid gap-1">
              <Label>Tipo appuntamento</Label>
              <RelationPicker
                value={appointmentTypeId}
                onChange={setAppointmentTypeId}
                search={searchAppointmentTypes}
                resolve={resolveAppointmentType}
                placeholder="Visita, valutazione…"
                aria-label="Tipo appuntamento"
              />
            </div>
          )}

          <div className="grid gap-1">
            <Label htmlFor="activity-title">Titolo</Label>
            <Input
              id="activity-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Facoltativo"
            />
          </div>

          <div className="grid gap-1">
            <Label htmlFor="activity-body">Testo</Label>
            <Textarea
              id="activity-body"
              aria-label="Testo attività"
              rows={3}
              placeholder="Cos’è successo, o cosa va fatto…"
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
          </div>
        </div>

        {save.isError && (
          <Alert tone="danger">
            <AlertDescription>
              {errorMessage(save.error, 'Non è stato possibile salvare l’attività.')}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={!canSubmit || save.isPending}
            onClick={submit}
          >
            {editing ? 'Salva' : 'Registra'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
